<template>
  <div class="cd-root">

    <!-- ============ PERSONAL (merged: contact + personal + address + security) ============ -->
    <div class="cd-section">
      <div class="cd-section-header">
        <i class="fa-solid fa-user cd-section-icon"></i>
        <h3 class="cd-section-title">{{ $t('personal') }}</h3>
      </div>
      <div class="cd-grid">
        <div v-if="disableManagement !== true" class="cd-row cd-row-readonly">
          <div class="cd-label">{{ $t('columns.id') }}</div>
          <div class="cd-value"><b-form-input size="sm" v-model="client.id" readonly/></div>
        </div>
        <div class="cd-row">
          <div class="cd-label">{{ $t('columns.firstName') }}<span class="cd-req">*</span></div>
          <div class="cd-value">
            <b-form-input size="sm" v-model="client.firstName" :state="stateFirstName"
                          :readonly="!hasPermission(permissions.CLIENT_UPDATE_CONTACT)"/>
          </div>
        </div>
        <div class="cd-row">
          <div class="cd-label">{{ $t('columns.lastName') }}<span class="cd-req">*</span></div>
          <div class="cd-value">
            <b-form-input size="sm" v-model="client.lastName" :state="stateLastName"
                          :readonly="!hasPermission(permissions.CLIENT_UPDATE_CONTACT)"/>
          </div>
        </div>
        <div class="cd-row">
          <div class="cd-label">{{ $t('columns.email') }}<span class="cd-req">*</span></div>
          <div class="cd-value">
            <b-input-group size="sm">
              <b-form-input v-if="hasPermission(permissions.CLIENT_READ_EMAIL)" v-model="client.email"
                            :state="stateEmail" :readonly="!hasPermission(permissions.CLIENT_UPDATE_CONTACT)"/>
              <b-form-input v-else v-model="noAccessData" readonly/>
              <b-input-group-append>
                <b-button variant="outline-primary" size="sm" @click="gotToChat" v-b-tooltip title="Chat">
                  <i class="fa-solid fa-comment"></i>
                </b-button>
                <b-button variant="primary" size="sm" @click="openEmailEditor"
                          v-b-tooltip :title="$t('operations.sendMail')">
                  <i class="fa-solid fa-envelope"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </div>
        </div>
        <div class="cd-row">
          <div class="cd-label">{{ $t('columns.email2') }}</div>
          <div class="cd-value">
            <b-form-input size="sm" v-if="hasPermission(permissions.CLIENT_READ_EMAIL)" v-model="client.email2"
                          :readonly="!hasPermission(permissions.CLIENT_UPDATE_CONTACT)"/>
            <b-form-input size="sm" v-else v-model="noAccessData" readonly/>
          </div>
        </div>
        <div class="cd-row">
          <div class="cd-label">{{ $t('columns.phone') }}</div>
          <div class="cd-value">
            <b-input-group size="sm">
              <b-form-input v-if="hasPermission(permissions.CLIENT_READ_PHONE)" v-model="client.phone"
                            :readonly="!hasPermission(permissions.CLIENT_UPDATE_CONTACT)"/>
              <b-form-input v-else v-model="noAccessData" readonly/>
              <b-input-group-append>
                <b-button variant="outline-primary" size="sm" :disabled="!hasPhoneToCall('phone') || callInProgress"
                          v-b-tooltip :title="$t('operations.call')" @click="initiateCallClick('phone')">
                  <b-spinner small v-if="callInProgress"/><i v-else class="fa-solid fa-phone-volume"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </div>
        </div>
        <div class="cd-row">
          <div class="cd-label">{{ $t('columns.mobile') }}</div>
          <div class="cd-value">
            <b-input-group size="sm">
              <b-form-input v-if="hasPermission(permissions.CLIENT_READ_PHONE)" v-model="client.mobile"
                            :readonly="!hasPermission(permissions.CLIENT_UPDATE_CONTACT)"/>
              <b-form-input v-else v-model="noAccessData" readonly/>
              <b-input-group-append>
                <b-button variant="outline-primary" size="sm" :disabled="!hasPhoneToCall('mobile') || callInProgress"
                          v-b-tooltip :title="$t('operations.call')" @click="initiateCallClick('mobile')">
                  <b-spinner small v-if="callInProgress"/><i v-else class="fa-solid fa-phone-volume"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </div>
        </div>
        <div class="cd-row">
          <div class="cd-label">{{ $t('columns.country') }}<span class="cd-req">*</span></div>
          <div class="cd-value">
            <countries-select-box small v-model="client.country" :state="stateCountry"
                                  :readonly="!hasPermission(permissions.CLIENT_UPDATE_CONTACT)"/>
          </div>
        </div>
        <div class="cd-row">
          <div class="cd-label">{{ $t('columns.gender') }}</div>
          <div class="cd-value">
            <b-form-input size="sm" v-model="client.gender"
                          :readonly="!hasPermission(permissions.CLIENT_UPDATE_CONTACT)"/>
          </div>
        </div>
        <div class="cd-row">
          <div class="cd-label">{{ $t('columns.birthday') }}</div>
          <div class="cd-value">
            <b-form-datepicker size="sm" v-model="client.birthday" date-format="yyyy-MM-dd"
                               placeholder="YYYY-MM-DD" today-button close-button class="w-100"
                               :show-decade-nav="true" :date-info-fn="null"
                               :disabled="!hasPermission(permissions.CLIENT_UPDATE_CONTACT)"
                               :value-as-date="false"/>
          </div>
        </div>
        <div v-if="disableManagement !== true" class="cd-row">
          <div class="cd-label">{{ $t('columns.address') }}</div>
          <div class="cd-value">
            <b-form-input size="sm" v-model="client.address"
                          :readonly="!hasPermission(permissions.CLIENT_UPDATE_CONTACT)"/>
          </div>
        </div>
        <div v-if="disableManagement !== true" class="cd-row">
          <div class="cd-label">{{ $t('columns.city') }}</div>
          <div class="cd-value">
            <b-form-input size="sm" v-model="client.city"
                          :readonly="!hasPermission(permissions.CLIENT_UPDATE_CONTACT)"/>
          </div>
        </div>
        <div v-if="disableManagement !== true" class="cd-row">
          <div class="cd-label">{{ $t('columns.zip') }}</div>
          <div class="cd-value">
            <b-form-input size="sm" v-model="client.zip"
                          :readonly="!hasPermission(permissions.CLIENT_UPDATE_CONTACT)"/>
          </div>
        </div>
        <div class="cd-row">
          <div class="cd-label">{{ $t('columns.password') }}</div>
          <div class="cd-value">
            <b-input-group size="sm">
              <b-form-input v-model="client.password"
                            :readonly="!hasPermission(permissions.CLIENT_UPDATE_CONTACT)"/>
              <b-input-group-append>
                <b-button variant="outline-primary" size="sm" @click="generatePassword"
                          v-b-tooltip title="Generate Password">
                  <i class="fa-solid fa-key"></i>
                </b-button>
                <b-dropdown size="sm" variant="primary">
                  <template #button-content><i class="fa-solid fa-envelope"></i></template>
                  <b-dropdown-item @click="passwordEvent(false)">Update</b-dropdown-item>
                  <b-dropdown-item @click="passwordEvent(true)">Update and Send Email</b-dropdown-item>
                </b-dropdown>
              </b-input-group-append>
            </b-input-group>
          </div>
        </div>
        <div v-if="disableManagement === true" class="cd-row">
          <div class="cd-label">{{ $t('columns.statusId') }}</div>
          <div class="cd-value">
            <client-status-picker small v-model="client.statusId"
                                  :readonly="!hasPermission(permissions.CLIENT_UPDATE_CONTACT)"/>
          </div>
        </div>
      </div>
    </div>

    <!-- ============ MANAGEMENT ============ -->
    <div v-if="disableManagement !== true" class="cd-section">
      <div class="cd-section-header">
        <i class="fa-solid fa-building cd-section-icon"></i>
        <h3 class="cd-section-title">{{ $t('tabs.clients.management') }}</h3>
      </div>
      <div class="cd-grid">
        <!-- Status first (most-edited) -->
        <div class="cd-row">
          <div class="cd-label">{{ $t('columns.statusId') }}</div>
          <div class="cd-value">
            <client-status-picker v-model="client.statusId"
                                  :readonly="!hasPermission(permissions.CLIENT_UPDATE_CONTACT)"/>
          </div>
        </div>
        <!-- Assignments grouped together -->
        <div class="cd-row">
          <div class="cd-label">{{ $t('columns.presaleUserId') }}</div>
          <div class="cd-value">
            <user-picker v-model="client.presaleUserId"
                         :disabled="!hasPermission(permissions.CLIENT_UPDATE_REPRESENTATIVE)"/>
          </div>
        </div>
        <div class="cd-row">
          <div class="cd-label">{{ $t('columns.representativeUserId') }}</div>
          <div class="cd-value">
            <user-picker v-model="client.representativeUserId"
                         :disabled="!hasPermission(permissions.CLIENT_UPDATE_REPRESENTATIVE)"/>
          </div>
        </div>
        <div class="cd-row">
          <div class="cd-label">{{ $t('columns.retentionUserId') }}</div>
          <div class="cd-value">
            <user-picker v-model="client.retentionUserId"
                         :disabled="!hasPermission(permissions.CLIENT_UPDATE_REPRESENTATIVE)"/>
          </div>
        </div>
        <!-- Org structure grouped -->
        <div v-if="hasManyBusinessUnits" class="cd-row">
          <div class="cd-label">{{ $t('columns.businessUnitId') }}</div>
          <div class="cd-value">
            <business-unit-select-box v-model="client.businessUnitId"
                                      :disabled="!hasPermission(permissions.CLIENT_UPDATE_BU)"/>
          </div>
        </div>
        <div v-if="hasManyOrganizations" class="cd-row">
          <div class="cd-label">{{ $t('columns.orgId') }}</div>
          <div class="cd-value">
            <organization-select-box v-model="client.orgId"
                                     :disabled="!hasPermission(permissions.CLIENT_UPDATE_ORG)" readonly/>
          </div>
        </div>
        <!-- VIP toggle -->
        <div class="cd-row">
          <div class="cd-label">VIP</div>
          <div class="cd-value">
            <b-form-checkbox v-model="client.vip"
                             :disabled="!hasPermission(permissions.CLIENT_UPDATE_VIP)" switch/>
          </div>
        </div>
        <!-- Audit dates at the bottom (read-only) -->
        <div class="cd-row cd-row-readonly">
          <div class="cd-label">{{ $t('columns.creationTime') }}</div>
          <div class="cd-value"><date-time-picker size="sm" v-model="client.creationTime" :initial-now-time="false" disabled/></div>
        </div>
        <div class="cd-row cd-row-readonly">
          <div class="cd-label">{{ $t('columns.ftdTime') }}</div>
          <div class="cd-value"><date-time-picker size="sm" v-model="client.ftdTime" :initial-now-time="false" disabled/></div>
        </div>
      </div>
    </div>

    <!-- ============ AFFILIATE ============ -->
    <div v-if="disableManagement !== true && hasPermission(permissions.CLIENT_READ_AFFILIATE)" class="cd-section">
      <div class="cd-section-header">
        <i class="fa-solid fa-person-shelter cd-section-icon"></i>
        <h3 class="cd-section-title">{{ $t('tabs.clients.affiliate') }}</h3>
      </div>
      <div class="cd-grid">
        <div class="cd-row">
          <div class="cd-label">{{ $t('columns.affiliateId') }}</div>
          <div class="cd-value"><affiliate-select-box v-model="client.affiliateId"/></div>
        </div>
        <div class="cd-row">
          <div class="cd-label">{{ $t('columns.campaign') }}</div>
          <div class="cd-value"><b-form-input size="sm" v-model="client.campaign"/></div>
        </div>
        <div class="cd-row">
          <div class="cd-label">{{ $t('columns.banner') }}</div>
          <div class="cd-value"><b-form-input size="sm" v-model="client.banner"/></div>
        </div>
        <div class="cd-row">
          <div class="cd-label">{{ $t('columns.tag') }}</div>
          <div class="cd-value"><b-form-input size="sm" v-model="client.tag"/></div>
        </div>
      </div>
    </div>

    <!-- ============ NOTES & EVENTS (inline — events left, notes right; expandable) ============ -->
    <div v-if="hasPermission(permissions.CLIENT_READ)" class="cd-collab">
      <div class="cd-collab-bar">
        <button type="button" class="cd-collab-toggle" @click="toggleNotesExpanded"
                v-b-tooltip :title="notesExpanded ? $t('operations.collapse') : $t('operations.expand')">
          <i class="fa-solid" :class="notesExpanded ? 'fa-compress' : 'fa-expand'"></i>
          <span>{{ notesExpanded ? $t('operations.collapse') : $t('operations.expand') }}</span>
        </button>
      </div>
      <b-row>
        <b-col cols="12" :lg="notesExpanded ? 12 : 6" :order="notesExpanded ? 2 : 1"
               class="mb-3" :class="{ 'mt-2': notesExpanded }">
          <client-events ref="events" :client-id="client.id" autoload/>
        </b-col>
        <b-col cols="12" :lg="notesExpanded ? 12 : 6" :order="notesExpanded ? 1 : 2" class="mb-3">
          <client-notes ref="notes" :client-id="client.id" autoload/>
        </b-col>
      </b-row>
    </div>

    <!-- ============ SAVE ============ -->
    <div class="cd-save-bar">
      <slot name="button" :update="update">
        <b-button block size="md" variant="primary" class="cd-save-btn" @click="update">
          <i class="fa-solid fa-floppy-disk mr-1"></i>{{ $t('operations.save') }}
        </b-button>
      </slot>
    </div>

    <email-editor-modal @emailTemplateSaved="sendEmail" ref="emailEditorModal" :receiver="client"/>

    <b-modal v-model="showCallConfirmModal" :title="$t('click2call.confirmTitle')" @ok="confirmCall"
             :ok-title="$t('click2call.initiate')" :cancel-title="$t('operations.cancel')">
      <p>{{ $t('click2call.confirmMessage', { number: pendingCallPhone }) }}</p>
    </b-modal>
  </div>
</template>

<script>
import Client from '@/model/client/Client';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import { mapActions } from 'vuex';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import OrganizationSelectBox from '@/components/widget/select/OrganizationSelectBox';
import BusinessUnitSelectBox from '@/components/widget/select/BusinessUnitSelectBox';
import CountriesSelectBox from '@/components/widget/select/CountriesSelectBox';
import mixinBase from '@/components/mixin/mixinBase';
import UserPicker from '@/components/widget/picker/userPicker';
import ClientStatusPicker from '@/components/widget/picker/clientStatusPicker';
import EmailEditorModal from '@/components/partial/mailTemplates/EmailEditorModal';
import ClientNotes from '@/components/partial/notes/notes';
import ClientEvents from '@/components/partial/notes/events';
import AffiliateSelectBox from '@/components/widget/select/AffiliateSelectBox.vue';
import DateTimePicker from '@/components/widget/picker/dateTimePicker.vue';

export default {
  name: 'ClientEditDetails',
  components: {
    DateTimePicker, UserPicker, EmailEditorModal, CountriesSelectBox,
    BusinessUnitSelectBox, OrganizationSelectBox, AffiliateSelectBox, ClientStatusPicker,
    ClientNotes, ClientEvents
  },
  mixins: [mixinBase, mixinPermissions, mixinNotifications],
  props: {
    value: { required: true, default: () => ({}) },
    disableManagement: { type: Boolean, default: false },
    small: { type: Boolean, default: false }
  },
  data: () => ({
    client: Client(),
    noAccessData: 'xxxxxx',
    sendEmailPassword: false,
    mandatoryFieldState: { stateFirstName: false, stateLastName: false, stateEmail: false, stateCountry: false },
    showCallConfirmModal: false,
    pendingCallPhone: '',
    callInProgress: false,
    notesExpanded: false
  }),
  watch: {
    value: {
      handler() { this.client = Object.assign(Client(), this.value); },
      immediate: true
    }
  },
  created() {
    try {
      this.notesExpanded = localStorage.getItem('client.notesExpanded') === 'true';
    } catch (e) {
      this.notesExpanded = false;
    }
  },
  computed: {
    stateFirstName() {
      const ok = this.client && this.client.firstName && this.client.firstName.length >= 1;
      this.mandatoryFieldState.stateFirstName = !!ok;
      return ok ? null : false;
    },
    stateLastName() {
      const ok = this.client && this.client.lastName && this.client.lastName.length >= 1;
      this.mandatoryFieldState.stateLastName = !!ok;
      return ok ? null : false;
    },
    stateEmail() {
      // Linear, backtracking-safe email check. The previous pattern had nested
      // quantifiers that caused catastrophic backtracking on some addresses,
      // surfacing in Firefox as "InternalError: too much recursion".
      const ok = this.client && this.client.email &&
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.client.email);
      this.mandatoryFieldState.stateEmail = !!ok;
      return ok ? null : false;
    },
    stateCountry() {
      const ok = this.client && this.client.country && this.client.country.length >= 1;
      this.mandatoryFieldState.stateCountry = !!ok;
      return ok ? null : false;
    }
  },
  methods: {
    ...mapActions('clients', ['updateClient', 'handlePasswordEvent', 'click2call']),

    toggleNotesExpanded() {
      this.notesExpanded = !this.notesExpanded;
      try {
        localStorage.setItem('client.notesExpanded', String(this.notesExpanded));
      } catch (e) { /* ignore quota / disabled storage */ }
    },
    hasPhoneToCall(field) {
      if (!this.hasPermission(this.permissions.CLIENT_READ_PHONE)) return false;
      const phone = field === 'phone' ? this.client.phone : this.client.mobile;
      return phone && String(phone).trim().length >= 6;
    },
    initiateCallClick(field) {
      const phone = field === 'phone' ? this.client.phone : this.client.mobile;
      if (!phone || !this.hasPhoneToCall(field)) return;
      this.pendingCallPhone = phone;
      this.pendingCallField = field;
      this.showCallConfirmModal = true;
    },
    confirmCall() {
      const phone = this.pendingCallPhone;
      this.showCallConfirmModal = false;
      this.callInProgress = true;
      const $this = this;
      this.click2call({ phoneNumber: phone, clientId: this.client.id })
        .then(() => {
          $this.showSuccessNotification($this.$t('click2call.success'));
          $this.pendingCallPhone = '';
        })
        .catch((err) => {
          $this.showErrorNotification($this.$t('click2call.error'),
              $this.getErrorDescription ? $this.getErrorDescription(err) : (err?.error || err?.message || String(err)));
        })
        .finally(() => { $this.callInProgress = false; });
    },
    openEmailEditor() { this.$refs.emailEditorModal.showModal = true; },
    /** Called from the modal's quick-action bar — opens the inline notes "add" modal. */
    openNoteModal() {
      const notes = this.$refs.notes;
      if (notes && typeof notes.openModal === 'function') notes.openModal();
    },
    /** Called from the modal's quick-action bar — opens the inline events "schedule" modal. */
    openEventModal() {
      const events = this.$refs.events;
      if (events && typeof events.openModal === 'function') events.openModal();
    },
    gotToChat() {
      this.$router.push({ path: '/clients/chat', query: { sessionClientId: this.client.id } });
      this.$emit('close');
    },
    sendEmail() { this.$refs.emailEditorModal.showModal = false; },
    generatePassword() {
      const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
      let p = '';
      for (let i = 0; i < 10; i++) p += charset.charAt(Math.floor(Math.random() * charset.length));
      this.client.password = p;
    },
    passwordEvent(sendMail) {
      const $this = this;
      this.handlePasswordEvent({ clientId: this.client.id, sendMail, password: this.client.password })
          .then(() => {
            if (sendMail) this.showSuccessNotification($this.$t('globalMessages.emailSentSuccessfully', { system: $this.$t('passwordSent') }));
            else this.showSuccessNotification($this.$t('globalMessages.updateDataSuccess', { system: $this.$t('menu.clients') }));
          }, (err) => {
            $this.showErrorNotification($this.$t('globalMessages.emailSentError', { system: $this.$t('passwordSent') }), err.error + ' ' + err.params);
          });
    },
    update() {
      if (!this.checkMandatory()) return;
      const $this = this;
      if (this.client.birthday) {
        const date = new Date(this.client.birthday);
        this.client.birthday = date.toISOString().split('T')[0];
      }
      return this.updateClient({ client: this.client, id: this.client.id })
          .then(() => {
            this.showSuccessNotification($this.$t('globalMessages.updateDataSuccess', { system: $this.$t('menu.clients') }));
            this.refreshTables();
            $this.$emit('input', this.client);
          })
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.clients') }), err.error + ' ' + err.params);
          });
    },
    checkMandatory() {
      for (const k in this.mandatoryFieldState) if (this.mandatoryFieldState[k] === false) return false;
      return true;
    }
  }
};
</script>

<style scoped>
.cd-root {
  --cd-bg: #ffffff;
  --cd-surface: rgba(37, 99, 235, 0.06);
  --cd-surface-edge: rgba(37, 99, 235, 0.16);
  --cd-text: #0f172a;
  --cd-text-2: #1f2937;
  --cd-text-muted: #94a3b8;
  --cd-border: #cbd5e1;
  --cd-border-strong: #94a3b8;
  --cd-divider: #e2e8f0;
  --cd-readonly-bg: rgba(37, 99, 235, 0.08);
  --cd-primary: #2563eb;
  --cd-primary-soft: #eff6ff;
  --cd-danger: #dc2626;

  padding: 18px 18px 80px;
  border-radius: 12px;
  color: var(--cd-text);
  background: var(--cd-surface);
  border: 1px solid var(--cd-surface-edge);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

.dark-layout .cd-root {
  --cd-bg: rgba(255, 255, 255, 0.045);
  --cd-surface: rgba(126, 192, 255, 0.07);
  --cd-surface-edge: rgba(126, 192, 255, 0.18);
  --cd-text: #f1f5f9;
  --cd-text-2: #cbd5e1;
  --cd-text-muted: #94a3b8;
  --cd-border: rgba(255, 255, 255, 0.10);
  --cd-border-strong: rgba(255, 255, 255, 0.24);
  --cd-divider: rgba(255, 255, 255, 0.08);
  --cd-readonly-bg: rgba(255, 255, 255, 0.025);
  --cd-primary: #93caff;
  --cd-primary-soft: rgba(147, 202, 255, 0.20);
}

/* ============ SECTION ============ */
.cd-section {
  margin-bottom: 32px;
}

.cd-section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 2px solid var(--cd-border);
}

.cd-section-icon {
  font-size: 0.95rem;
  color: var(--cd-primary);
  background: var(--cd-primary-soft);
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cd-section-title {
  font-size: 1.02rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--cd-text);
  margin: 0;
}

/* ============ GRID — denser, more fields per row ============ */
.cd-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 22px;
}

@media (min-width: 900px) {
  .cd-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

@media (min-width: 1180px) {
  .cd-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}

@media (max-width: 600px) {
  .cd-grid { grid-template-columns: 1fr; }
}

/* Each row aligns label : value with consistent column widths */
.cd-row {
  display: grid;
  grid-template-columns: 90px 1fr;
  gap: 8px;
  align-items: center;
  padding: 5px 0;
  min-width: 0;
}

.cd-row-wide {
  grid-column: 1 / -1;
  grid-template-columns: 90px 1fr;
}

.cd-row-readonly { opacity: 0.85; }

.cd-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--cd-text-2);
  letter-spacing: 0.2px;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cd-req {
  color: var(--cd-danger);
  margin-left: 2px;
  font-weight: 700;
}

.cd-value {
  min-width: 0;
}

/* High-contrast inputs — white filled rectangles like phone/mobile */
.cd-value >>> .form-control,
.cd-value >>> .b-form-datepicker.form-control {
  background-color: var(--cd-bg) !important;
  border: 1px solid var(--cd-border);
  border-radius: 6px;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  font-size: 0.9rem;
  height: 30px;
  min-height: 30px;
  padding: 2px 10px;
  line-height: 1.3;
  color: var(--cd-text);
}

/* b-form-datepicker has internal label and button — make sure they share the input bg */
.cd-value >>> .b-form-datepicker label {
  background-color: transparent !important;
  color: var(--cd-text) !important;
  font-size: 0.9rem;
  padding: 2px 8px;
  line-height: 26px;
}

.cd-value >>> .b-form-datepicker .btn {
  background-color: transparent !important;
  color: var(--cd-text-2) !important;
  border: none;
  padding: 2px 8px;
  height: 26px;
}

.cd-value >>> .b-form-datepicker .btn:hover {
  color: var(--cd-primary) !important;
}

.cd-value >>> .form-control:hover,
.cd-value >>> .b-form-datepicker:hover {
  border-color: var(--cd-border-strong);
}

.cd-value >>> .form-control:focus,
.cd-value >>> .b-form-datepicker:focus,
.cd-value >>> .b-form-datepicker.focus {
  background: var(--cd-bg);
  border-color: var(--cd-primary);
  box-shadow: 0 0 0 3px var(--cd-primary-soft);
  outline: none;
}

.cd-value >>> .form-control[readonly] {
  background: var(--cd-readonly-bg);
  color: var(--cd-text-2);
  cursor: default;
}

.cd-value >>> .input-group {
  flex-wrap: nowrap;
}

.cd-value >>> .input-group .form-control {
  border-color: var(--cd-border);
  background: var(--cd-bg);
  height: 30px;
}

.cd-value >>> .input-group .input-group-append .btn {
  border-radius: 0 6px 6px 0;
  height: 30px;
  padding: 0 10px;
  font-size: 0.78rem;
  line-height: 1;
}

/* v-select pickers (user/affiliate/business-unit/organization/status) */
.cd-value >>> .v-select .vs__dropdown-toggle {
  min-height: 30px;
  padding: 0 4px 0 6px;
  border: 1px solid var(--cd-border);
  background: var(--cd-bg);
  border-radius: 6px;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}
.cd-value >>> .v-select .vs__selected,
.cd-value >>> .v-select .vs__search {
  font-size: 0.9rem;
  margin-top: 2px;
  line-height: 1.3;
  padding: 0;
  color: var(--cd-text);
}
.cd-value >>> .v-select .vs__actions {
  padding: 0 4px 0 0;
}
.cd-value >>> .v-select:hover .vs__dropdown-toggle {
  border-color: var(--cd-border-strong);
}
.cd-value >>> .v-select.vs--open .vs__dropdown-toggle,
.cd-value >>> .v-select:focus-within .vs__dropdown-toggle {
  background: var(--cd-bg);
  border-color: var(--cd-primary);
  box-shadow: 0 0 0 3px var(--cd-primary-soft);
}

/* ============ NOTES + EVENTS ============ */
.cd-collab {
  margin-top: 8px;
  padding-top: 18px;
  border-top: 2px solid var(--cd-border);
}

.cd-collab-bar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.cd-collab-toggle {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: var(--cd-bg);
  border: 1px solid var(--cd-border);
  color: var(--cd-text-2);
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.cd-collab-toggle:hover {
  border-color: var(--cd-primary);
  color: var(--cd-primary);
  background: var(--cd-primary-soft);
}

.cd-collab-toggle:focus-visible {
  outline: none;
  border-color: var(--cd-primary);
  box-shadow: 0 0 0 3px var(--cd-primary-soft);
}

/* ============ SAVE BAR ============ */
.cd-save-bar {
  position: sticky;
  bottom: 0;
  background: linear-gradient(180deg, transparent 0%, var(--cd-surface) 40%);
  padding: 14px 0 4px;
  margin-top: 18px;
  z-index: 2;
}

.cd-save-btn {
  border-radius: 9px;
  padding: 9px 0;
  font-weight: 600;
  font-size: 0.92rem;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.25);
}
</style>
